/**
 * Best-effort transport for perf beacons (AXI-3875).
 *
 * Events are buffered and flushed on a short debounce, plus eagerly on
 * `visibilitychange → hidden` / `pagehide` so the last screen's numbers
 * survive a tab close or navigation away. Delivery uses `navigator.sendBeacon`
 * (survives unload, no response handling) with a `fetch(..., keepalive)`
 * fallback.
 *
 * The body is sent as `text/plain` — a CORS-safelisted content type — so the
 * cross-origin POST to the api-service skips the preflight that an
 * `application/json` beacon would trigger (and which `sendBeacon` cannot
 * negotiate). The collector parses the text body as JSON. `sendBeacon` also
 * cannot set request headers, which is why the navigation traceparent travels
 * inside the JSON payload, not in a `traceparent` header.
 */
import { getTelemetryCollectorUrl, isPerfTelemetryEnabled } from '../config'
import type { TelemetryBatch, TelemetryBeacon } from './types'

const FLUSH_DEBOUNCE_MS = 2_000
// Cap the buffer so a misbehaving page can never grow it without bound; oldest
// events are dropped first (web-vitals/nav data is only useful fresh).
const MAX_BUFFERED_EVENTS = 100

let buffer: TelemetryBeacon[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let unloadListenersInstalled = false

function canSend(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    isPerfTelemetryEnabled() &&
    getTelemetryCollectorUrl().length > 0
  )
}

function send(batch: TelemetryBatch): void {
  const url = getTelemetryCollectorUrl()
  const body = JSON.stringify(batch)
  // `text/plain` keeps the request CORS-simple (no preflight).
  const blob = new Blob([body], { type: 'text/plain' })

  if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url, blob)) {
    return
  }
  // Fallback for browsers/contexts where sendBeacon is unavailable or refused
  // (e.g. buffer full). keepalive lets it outlive an unloading document.
  if (typeof fetch === 'function') {
    void fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain' },
      keepalive: true
    }).catch(() => undefined)
  }
}

/** Flush the buffer immediately. No-op when empty or when telemetry is off. */
export function flushBeacons(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (buffer.length === 0) return
  const events = buffer
  buffer = []
  if (!canSend()) return
  send({ events })
}

function installUnloadFlush(): void {
  if (unloadListenersInstalled || typeof window === 'undefined') return
  unloadListenersInstalled = true
  // `visibilitychange → hidden` is the reliable last-chance hook on mobile and
  // desktop; `pagehide` covers bfcache eviction. Both just drain the buffer.
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacons()
  })
  window.addEventListener('pagehide', () => flushBeacons())
}

/** Queue one beacon for delivery, scheduling a debounced flush. */
export function enqueueBeacon(event: TelemetryBeacon): void {
  if (!canSend()) return
  installUnloadFlush()
  buffer.push(event)
  if (buffer.length > MAX_BUFFERED_EVENTS) {
    buffer.splice(0, buffer.length - MAX_BUFFERED_EVENTS)
  }
  if (!flushTimer) {
    flushTimer = setTimeout(flushBeacons, FLUSH_DEBOUNCE_MS)
  }
}

/** Test seam: drop any buffered events and pending timer without sending. */
export function __resetBeaconBufferForTests(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  buffer = []
}
